import find from "lodash/find";
import map from "lodash/map";

import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import { useEffect } from "react";

import type { Question } from "@/types";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

import {
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  FormErrorIcon,
  HTMLWrapper,
  Select,
  useBreakpointValue,
  VisuallyHidden
} from "@/storybook-components";

interface DropDownQuestionProps {
  question: Question;
}

export const validate = async (
  answer: string,
  question: Question
): Promise<string[]> => {
  let schema = string().nullable();
  if (question.required) {
    schema = schema.required();
  }
  try {
    await schema.validate(answer);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const DropDownQuestion = ({ question }: DropDownQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const size = useBreakpointValue({ base: "lg", lg: "md" });
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const answer = answers[question.id] || "";

  const choice = find(
    question.questionOptions.filter((option) => !option.deleted),
    ({ id }) => id === answer
  );
  const answeredOption = choice?.name
    ? { label: choice.name, value: answer }
    : null;

  useEffect(() => {
    if (
      map(
        question.questionOptions.filter((option) => option.deleted),
        "id"
      ).includes(answer)
    ) {
      dispatch(
        setAnswer({
          questionId: question.id,
          answer: null
        })
      );
    }
  }, [answer, choice, dispatch, question.id, question.questionOptions]);

  const handleChange = (e: { value: string }): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e?.value ? parseInt(e.value) : null
      })
    );
  };

  return (
    <FormControl
      my={4}
      id={`${question.id}`}
      isInvalid={error.length > 0}
      isRequired={question.required}
      isDisabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <FormLabel>
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </FormLabel>
      <Select
        size={size}
        isClearable
        menuPlacement="auto"
        isSearchable={false}
        value={answeredOption}
        onChange={handleChange}
        classNamePrefix="ehq-dropdown"
        className="ehq-dropdownContainer"
        placeholder={translate("questions.dropdown.selectOption")}
        innerProps={{ "data-testid": "dropdownQuestion-select" }}
        options={[...question.questionOptions]
          .filter((option) => !option.deleted)
          .sort((a, b) => a.sequence - b.sequence)
          .map((option) => ({ value: option.id, label: option.name }))}
        name="dropdownQuestion-select"
        autoComplete="off"
      />
      {error.length > 0 && (
        <FormErrorMessage data-testid="dropdownQuestion-errorMessage">
          <FormErrorIcon />
          {translate(`errors.${error}`)}
        </FormErrorMessage>
      )}
      <FormHelperText>{question.notes}</FormHelperText>
    </FormControl>
  );
};

export default DropDownQuestion;
